import { canUseCookies } from './cookies.js'

/**
 * Оборачивает функцию работы с `localStorage` проверкой согласия на
 * функциональные cookie.
 *
 * @template {Function} T
 * @param {T} fn Функция, которую можно выполнять только после согласия.
 * @param {Object} [options={}] Настройки проверки согласия.
 * @param {string} [options.category='functionality'] Категория cookie consent.
 * @param {*|Function} [options.fallback=null] Значение или функция fallback,
 *   которые будут использованы при отсутствии согласия.
 * @returns {Function} Функция-обертка с проверкой согласия.
 */
function withCookieConsent(fn, options = {}) {
  const {
    category = 'functionality', // Категория по умолчанию
    fallback = null
  } = options

  return function (...args) {
    if (!canUseCookies(category)) {
      return typeof fallback === 'function' ? fallback(...args) : fallback
    }

    return fn(...args)
  }
}

/**
 * Записывает значение в `localStorage` без проверки согласия.
 *
 * @param {string} key Ключ записи.
 * @param {string} value Значение записи.
 * @returns {void}
 */
function setItemImpl(key, value) {
  try {
    localStorage.setItem(key, value)
  } catch (error) {
    console.error(`Failed to set item in localStorage: ${error}`)
  }
}

/**
 * Читает значение из `localStorage` без проверки согласия.
 *
 * @param {string} key Ключ записи.
 * @returns {string|null} Значение записи или `null`, если ключ не найден.
 */
function getItemImpl(key) {
  try {
    return localStorage.getItem(key)
  } catch (error) {
    console.error(`Failed to get item from localStorage: ${error}`)
    return null
  }
}

/**
 * Записывает значение в `localStorage`, если пользователь разрешил категорию
 * `functionality`.
 *
 * @type {(key: string, value: string) => void|null}
 */
export const setItem = withCookieConsent(setItemImpl, {
  category: 'functionality',
  fallback: () => {
    console.warn(
      'Cannot set item in localStorage because user has not given consent for functionality cookies.'
    )
    return null
  }
})

/**
 * Читает значение из `localStorage`, если пользователь разрешил категорию
 * `functionality`.
 *
 * @type {(key: string) => string|null}
 */
export const getItem = withCookieConsent(getItemImpl, {
  category: 'functionality',
  fallback: () => {
    console.warn(
      'Cannot get item from localStorage because user has not given consent for functionality cookies.'
    )
    return null
  }
})
